import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { isBlank } from '@ember/utils';
import { action, get } from '@ember/object';
import { service } from '@ember/service';
import ObjectProxy from '@ember/object/proxy';
import { task, restartableTask, all } from 'ember-concurrency';
import employeesSearcherTask from 'garaje/utils/employees-searcher';
import { or, empty, filter, reads } from 'macro-decorators';

const wrapLocation = function (location) {
  return ObjectProxy.create({
    content: location,
    fullName: `${get(location, 'companyName')} ${get(location, 'name')}`,
    disabled: false, // Ember power select has a conflicting property name
  });
};

export default class MeetingController extends Controller {
  @service currentLocation;
  @service flashMessages;
  @service locations;
  @service store;
  @service logger;
  @service router;

  sendGuestNotification = false;
  defaultAddressLabel = 'No address given';

  @tracked validated = false;
  @tracked employee;
  @tracked selectedLocation;
  @tracked selectedFlow;

  @or('selectedLocation.address', 'defaultAddressLabel') meetingAddress;
  @empty('attendeesWithoutNames') allAttendeesHaveNames;
  @filter('model.attendees.content', (attendee) => isBlank(attendee.name)) attendeesWithoutNames;
  @reads('selectedLocation.id') selectedLocationId;

  setupLocation() {
    // This will default to the current location if location on the meeting is not loaded or null
    const selectedLocation = get(this.model, 'location.id') && get(this.model, 'location');
    const selectedOrDefaultLocation = selectedLocation || this.currentLocation;
    this.selectedLocation = wrapLocation(selectedOrDefaultLocation);
    this.selectedFlow = undefined;
  }

  get availableLocations() {
    const availableLocations = get(this.locations, 'readableByCurrentAdmin').filter(
      (location) => location.preRegistrationEnabled,
    );
    // There is a naming conflict with location object and ember power select
    // where the "disabled" property on locations disables the options in the UI
    return availableLocations.map((location) => wrapLocation(location));
  }

  @action
  setEmployee(employee) {
    this.model.employee = employee;
    this.employee = employee;
  }

  @action
  setSelectedLocation(location) {
    this.selectedLocation = location;
    this.selectedFlow = undefined;
    this.model.flowName = null;
    this.clearInvalidHost.perform();
  }

  @action
  setFlow(flow) {
    this.selectedFlow = flow;
    this.model.flowName = flow.name;
  }

  @(employeesSearcherTask({
    filter: {
      deleted: false,
      excludeHidden: true,
    },
    prefix: true,
  }).restartable())
  _searchEmployeesTask;

  registerTask = restartableTask(async () => {
    const { selectedLocationId } = this;
    const location = await this.store.findRecord('location', selectedLocationId);
    const employee = this.employee || (await get(this.model, 'employee'));
    const hasMVTSetIfRequired = this.model.flowName;
    const meetingHasEmployee = employee ? true : false;
    const validationError = !(meetingHasEmployee && this.allAttendeesHaveNames) || !hasMVTSetIfRequired;

    this.model.location = location;

    if (validationError) {
      this.validated = true;
      this.logger.info(
        'Meeting form failed to validate https://www.pivotaltracker.com/n/projects/1610817/stories/139252355',
      );
      this.flashMessages.showFlash('error', 'Please fill all the required fields.');
    } else {
      await all([this.model.save(), this.saveAttendees.perform(), this.createInvites.perform(location)]);
      this.router.transitionTo('visitors.invites.index');
    }
  });

  searchEmployees = restartableTask(async (term) => {
    const extraFilters = {
      locations: this.selectedLocationId,
    };
    return await this._searchEmployeesTask.perform(term, extraFilters);
  });

  saveAttendees = task(async () => {
    const attendees = await get(this.model, 'attendees');
    await attendees.save();
  });

  createInvites = task(async (location) => {
    const invitedAttendees = await get(this.model, 'attendees');

    const deleteExistingLocations = invitedAttendees.reduce((acc, attendee) => {
      if (attendee.hasInvite) {
        acc.push(this.deleteInvite.perform(attendee));
      }
      return acc;
    }, []);

    const createNewInvites = invitedAttendees.reduce((acc, attendee) => {
      if (attendee.sendInvite) {
        acc.push(this.createNewInvite.perform({ attendee, location }));
      }
      return acc;
    }, []);

    try {
      await all([...deleteExistingLocations, ...createNewInvites]);
      this.flashMessages.showAndHideFlash('success', 'Saved!');
    } catch (e) {
      this.flashMessages.showAndHideFlash('error', 'There was an error saving your invites');
    }
  });

  deleteInvite = task(async (attendee) => {
    const invite = await get(attendee, 'invite');
    await invite.destroyRecord();
  });

  createNewInvite = task(async ({ attendee, location }) => {
    const employee = this.employee || (await get(this.model, 'employee'));
    const invite = await location.createInvite({
      expectedArrivalTime: this.model.expectedArrivalTime,
      fullName: attendee.name,
      email: attendee.email,
    });

    invite.attendee = attendee;
    invite.skipGuestNotification = !this.sendGuestNotification;
    invite.flowName = this.model.flowName;

    const flow = this.selectedFlow || (await get(location, 'flows')).findBy('name', this.model.flowName);
    if (flow) invite.flow = flow;

    if (employee) {
      invite.employee = employee;
      invite.inviterName = employee.name;
    }

    await invite.save(); // Update with the attendee relationship
  });

  clearInvalidHost = task(async () => {
    const employee = this.employee || (await get(this.model, 'employee'));

    // No need to clear if there is no employee
    if (!employee) return;

    const employeeLocations = await get(employee, 'locations');
    const { selectedLocationId } = this;
    const employeeBelongsToSelectedLocation = employeeLocations.find((location) => location.id === selectedLocationId);

    if (!employeeBelongsToSelectedLocation) this.setEmployee(null);
  });
}
